import React from "react";
import * as api from "../../services/api";
import { useLatest } from "../../hooks";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { FileLink } from "../File";
import * as f from "../../utils/formatter";
import * as fe from "../FileExport";
import { parseUtcDateTime } from "shared/src/date.mjs";

function useFileCollections(client_id) {
  const [state, setState] = React.useState({
    loading: true,
    fileCollections: [],
  });

  useLatest(
    async (signal) => {
      try {
        setState((state) => ({ ...state, loading: true }));
        const result = await api.loadClientDocumentsFileCollections(
          { client_id },
          signal,
        );

        setState((state) => ({
          ...state,
          fileCollections: result.data?.client?.fileCollections ?? [],
        }));
      } finally {
        if (!signal.aborted) {
          setState((state) => ({ ...state, loading: false }));
        }
      }
    },
    [client_id, setState],
  );

  return state;
}

function Document({ client_id, fileCollection, exportState, setExportState }) {
  return (
    <Card>
      <CardContent>
        <Typography variant="h2" sx={{ fontWeight: 400, marginBottom: 3 }}>
          {fileCollection.class_name}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Box>
            <Typography variant="h3">
              {f.shortDateTime(parseUtcDateTime(fileCollection.created_at))}
            </Typography>
            <Typography>
              {`Stav: ${f.value(fileCollection.state_name)}`}
            </Typography>
          </Box>
          <Box display="flex" flexWrap="wrap" gap={2}>
            {fileCollection.files.map((f) => {
              const fileInfo = {
                type: fileCollection.type,
                objectid: fileCollection.id,
                file: f.id,
              };

              return (
                <fe.Selectable
                  key={f.id}
                  enabled={exportState.active && !exportState.loading}
                  selected={fe.isSelected(exportState, fileInfo)}
                  onSelect={() =>
                    setExportState((state) =>
                      fe.toggleSelection(state, fileInfo),
                    )
                  }
                >
                  <FileLink
                    key={f.id}
                    fileInfo={fileInfo}
                    details={f}
                    client_id={client_id}
                    fileCollection={fileCollection}
                    readOnly={exportState.active}
                  />
                </fe.Selectable>
              );
            })}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function Content({ client_id, fileCollections }) {
  const [exportState, setExportState] = fe.useExportState();

  return (
    <>
      <Box display="flex" justifyContent="space-between" sx={{ mt: 2, mb: 3 }}>
        <Typography variant="h1">Dokumenty</Typography>
        <fe.ExportButton
          client_id={client_id}
          exportState={exportState}
          setExportState={setExportState}
        />
      </Box>
      <Stack gap={2}>
        {fileCollections.map((fc) => (
          <Document
            key={`${fc.type}.${fc.id}`}
            client_id={client_id}
            fileCollection={fc}
            setExportState={setExportState}
            exportState={exportState}
          />
        ))}
      </Stack>
    </>
  );
}

export function Documents({ client_id }) {
  const { fileCollections, loading } = useFileCollections(client_id);

  if (loading) {
    return <CircularProgress />;
  }

  return <Content client_id={client_id} fileCollections={fileCollections} />;
}
